import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PageShell from "@/components/PageShell";
import LinkedText from "@/components/LinkedText";
import { ReadAloud } from "@/components/ReadAloud";
import api from "@/lib/api";
import { Check, Circle, ArrowLeft, Clock, Gavel, Bookmark, BookmarkCheck, Rabbit, ArrowRight, Scale, ExternalLink } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

export default function CourseDetail() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [active, setActive] = useState(0);
  const [completed, setCompleted] = useState([]);
  const [savedCases, setSavedCases] = useState(new Set());
  const [allCourses, setAllCourses] = useState([]);
  const [statutes, setStatutes] = useState([]);
  const [savedStatutes, setSavedStatutes] = useState(new Set());

  useEffect(() => {
    api.get(`/courses/${slug}`).then((r) => setCourse(r.data));
    api.get("/progress").then((r) => setCompleted(r.data.lessons || [])).catch(() => {});
    api.get(`/bookmarks?course=${slug}`).then((r) => {
      setSavedCases(new Set(r.data.map((b) => b.title)));
      setSavedStatutes(new Set(r.data.filter((b) => b.statute_id).map((b) => b.statute_id)));
    }).catch(() => {});
    api.get("/courses").then((r) => setAllCourses(r.data)).catch(() => {});
    api.get(`/statutes?course=${slug}`).then((r) => setStatutes(r.data.items || [])).catch(() => {});
  }, [slug]);

  const saveCase = async (c) => {
    try {
      await api.post("/bookmarks", { kind: "case", title: c.name, citation: c.citation, content: c.summary, course_slug: slug });
      setSavedCases((s) => new Set(s).add(c.name));
      toast.success("Saved to course research");
    } catch { toast.error("Could not save"); }
  };

  const saveStatute = async (s) => {
    try {
      await api.post("/bookmarks", { kind: "statute", title: s.title, citation: s.citation, url: s.url, statute_id: s.id, content: s.summary, course_slug: slug });
      setSavedStatutes((prev) => new Set(prev).add(s.id));
      toast.success("Saved to course research");
    } catch { toast.error("Could not save"); }
  };

  if (!course) return <PageShell><div className="max-w-7xl mx-auto px-6 py-20 text-muted-foreground">Loading…</div></PageShell>;

  const lesson = course.lessons[active];
  const key = `${course.slug}:${lesson.id}`;
  const isDone = completed.includes(key);

  const markComplete = async () => {
    try {
      const { data } = await api.post("/progress/lesson", { course_id: course.slug, lesson_id: lesson.id });
      setCompleted(data.lessons);
      toast.success("Lesson marked complete");
    } catch {
      toast.error("Could not save progress");
    }
  };

  return (
    <PageShell>
      <div className="max-w-7xl mx-auto px-6 py-10">
        <Link to="/courses" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> All courses
        </Link>

        <div className="grid lg:grid-cols-3 gap-px bg-border border border-border">
          {/* Sidebar curriculum */}
          <aside className="bg-card p-6 lg:max-h-[75vh] lg:overflow-y-auto">
            <span className="text-[10px] uppercase tracking-widest font-mono text-primary">{course.category}</span>
            <h1 className="font-serif text-2xl mt-1 mb-1 leading-tight">{course.title}</h1>
            <p className="text-xs text-muted-foreground mb-4">{course.subtitle}</p>
            <ReadAloud
              className="mb-6 w-full justify-center border border-primary/40 !text-primary py-2 rounded-sm hover:bg-primary/10"
              label="Listen to full course"
              getText={() => `${course.title}. ${course.subtitle}. ${course.summary} ${course.lessons.map((l) => `${l.title}. ${l.body}`).join(" ")}`}
            />
            <div className="space-y-1">
              {course.lessons.map((l, i) => {
                const lk = `${course.slug}:${l.id}`;
                const dn = completed.includes(lk);
                return (
                  <button
                    key={l.id}
                    onClick={() => setActive(i)}
                    data-testid={`lesson-nav-${i}`}
                    className={`w-full text-left flex items-start gap-3 px-3 py-3 rounded-sm transition-colors ${active === i ? "bg-muted" : "hover:bg-muted/50"}`}
                  >
                    {dn ? <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" /> : <Circle className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />}
                    <div>
                      <div className={`text-sm leading-snug ${active === i ? "text-foreground" : "text-muted-foreground"}`}>{l.title}</div>
                      <div className="text-[10px] font-mono text-muted-foreground mt-1 flex items-center gap-1"><Clock className="w-3 h-3" />{l.duration}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>

          {/* Main content */}
          <section className="bg-card lg:col-span-2 p-8 lg:p-12">
            <AnimatePresence mode="wait">
              <motion.article
                key={active}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs uppercase tracking-widest font-mono text-primary">Lesson {active + 1}</span>
                  <span className="text-xs font-mono text-muted-foreground">{lesson.duration}</span>
                  <ReadAloud className="ml-auto" label="Read lesson" getText={() => `${lesson.title}. ${lesson.body}`} />
                </div>
                <h2 className="font-serif text-4xl mb-6 leading-tight">{lesson.title}</h2>
                <div className="prose-none text-[15px] leading-[1.9] text-foreground/90 whitespace-pre-line"><LinkedText text={lesson.body} /></div>

                <div className="mt-10 flex items-center gap-4 border-t border-border pt-8">
                  <button
                    onClick={markComplete}
                    disabled={isDone}
                    data-testid="mark-complete"
                    className="bg-primary text-primary-foreground px-5 py-2.5 rounded-sm text-sm flex items-center gap-2 hover:-translate-y-[1px] transition-transform disabled:opacity-60"
                  >
                    <Check className="w-4 h-4" /> {isDone ? "Completed" : "Mark complete"}
                  </button>
                  {active < course.lessons.length - 1 && (
                    <button onClick={() => setActive(active + 1)} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                      Next lesson →
                    </button>
                  )}
                </div>
              </motion.article>
            </AnimatePresence>

            {course.cases && course.cases.length > 0 && (
              <div className="mt-10 border-t border-border pt-8">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2 text-primary">
                    <Gavel className="w-5 h-5" />
                    <h3 className="font-serif text-2xl text-foreground">Cases This Course Cites</h3>
                  </div>
                  <Link to={`/research?course=${course.slug}`} data-testid="course-research-link" className="text-xs uppercase tracking-widest font-mono text-muted-foreground hover:text-primary transition-colors">
                    My research →
                  </Link>
                </div>
                <div className="space-y-3">
                  {course.cases.map((c, i) => (
                    <div key={i} className="bg-muted border border-border p-5" data-testid={`course-case-${i}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="font-serif text-lg leading-tight">{c.name}</div>
                          <div className="text-xs font-mono text-muted-foreground mt-0.5">{c.citation}</div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <button onClick={() => saveCase(c)} data-testid={`course-case-save-${i}`} title="Save to research"
                            className={`transition-colors ${savedCases.has(c.name) ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>
                            {savedCases.has(c.name) ? <Check className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                          </button>
                          <Link to={`/caselaw?q=${encodeURIComponent(c.query || c.name)}&course=${course.slug}`} data-testid={`course-case-link-${i}`}
                            className="text-xs uppercase tracking-widest font-mono text-primary hover:underline whitespace-nowrap">
                            Read →
                          </Link>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground mt-2 leading-relaxed"><LinkedText text={c.summary} /></p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {statutes.length > 0 && (
              <div className="mt-10 border-t border-border pt-8" data-testid="course-statutes">
                <div className="flex items-center gap-2 mb-1 text-primary">
                  <Scale className="w-5 h-5" />
                  <h3 className="font-serif text-2xl text-foreground">Statutes &amp; authorities cited</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-5">
                  The Acts, U.S. Code and UCC behind this course. Save any to your Research folder or read the full text.
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {statutes.map((s) => (
                    <div key={s.id} data-testid={`course-statute-${s.id}`} className="bg-card border border-border p-4 flex flex-col">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="font-serif text-lg leading-tight">{s.title}</div>
                          <div className="text-xs font-mono text-primary/80 mt-0.5">{s.citation}</div>
                        </div>
                        <button onClick={() => saveStatute(s)} disabled={savedStatutes.has(s.id)} data-testid={`course-statute-save-${s.id}`}
                          title={savedStatutes.has(s.id) ? "Saved to research" : "Save to research"}
                          className={`shrink-0 ${savedStatutes.has(s.id) ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>
                          {savedStatutes.has(s.id) ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                        </button>
                      </div>
                      <p className="text-sm text-muted-foreground mt-2 leading-relaxed line-clamp-3 flex-1">{s.summary}</p>
                      <div className="flex items-center gap-4 mt-3">
                        <Link to={`/statutes?q=${encodeURIComponent(s.citation)}`} data-testid={`course-statute-open-${s.id}`}
                          className="text-[10px] uppercase tracking-widest font-mono text-primary hover:underline inline-flex items-center gap-1">
                          Open in library <ArrowRight className="w-3 h-3" />
                        </Link>
                        <a href={s.url} target="_blank" rel="noreferrer" className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground hover:text-primary inline-flex items-center gap-1">
                          Cornell LII <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {allCourses.length > 0 && (() => {
              const curated = course.rabbit_hole && course.rabbit_hole.length > 0 ? course.rabbit_hole : null;
              const related = curated ? [] : [
                ...allCourses.filter((c) => c.slug !== course.slug && c.track === course.track),
                ...allCourses.filter((c) => c.slug !== course.slug && c.track !== course.track),
              ].slice(0, 3);
              const firstCase = course.cases && course.cases[0];
              return (
                <div className="mt-10 border-t border-border pt-8" data-testid="rabbit-hole">
                  <div className="flex items-center gap-2 mb-1 text-primary">
                    <Rabbit className="w-5 h-5" />
                    <h3 className="font-serif text-2xl text-foreground">Continue down the rabbit hole</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-5">Each thread pulls another. Follow where it leads.</p>
                  {curated ? (
                    <div className="grid sm:grid-cols-3 gap-px bg-border border border-border">
                      {curated.map((r, i) => (
                        <Link key={i} to={r.to} data-testid={`rabbit-link-${i}`} className="bg-card p-5 group hover:bg-muted transition-colors">
                          <div className="font-serif text-lg leading-tight group-hover:text-primary transition-colors">{r.label}</div>
                          {r.note && <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{r.note}</p>}
                          <span className="mt-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-mono text-primary">Follow <ArrowRight className="w-3 h-3" /></span>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <>
                      <div className="grid sm:grid-cols-3 gap-px bg-border border border-border mb-4">
                        {related.map((c) => (
                          <Link key={c.slug} to={`/courses/${c.slug}`} data-testid={`rabbit-course-${c.slug}`} className="bg-card p-5 group hover:bg-muted transition-colors">
                            <span className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">{c.category}</span>
                            <div className="font-serif text-lg leading-tight mt-1 group-hover:text-primary transition-colors">{c.title}</div>
                          </Link>
                        ))}
                      </div>
                      <div className="flex flex-wrap gap-3">
                        <Link to="/dictionary" className="text-xs uppercase tracking-widest font-mono border border-border px-4 py-2.5 hover:border-primary hover:text-primary transition-colors">Explore the dictionary →</Link>
                        {firstCase && <Link to={`/caselaw?q=${encodeURIComponent(firstCase.query || firstCase.name)}&course=${course.slug}`} className="text-xs uppercase tracking-widest font-mono border border-border px-4 py-2.5 hover:border-primary hover:text-primary transition-colors">Dig into the case law →</Link>}
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
          </section>
        </div>
      </div>
    </PageShell>
  );
}
